import { useMemo, useState } from 'react';

import type { EChartsOption } from 'echarts';
import ReactECharts from 'echarts-for-react';

import { Loader } from '@/components/Loader';
import { graphConfig } from '@/configs/Graphs';
import { reduceToPoints, type Point } from '@/features/forecasts/components/forecast-details/FctForecastChart';
import { LegendToggle } from '@/features/forecasts/components/forecast-details/LegendToggle';
import {
  BFG_COLOR,
  BFG_MODEL_CONFIG,
  INCUMBENT_COLOR,
  INCUMBENT_MODEL_CONFIG,
  toModelLeadtimeSpans,
} from '@/features/forecasts/config/fctRuns';
import type { EventBehaviorTimeseriesPoint } from '@/features/forecasts/forecasts.api.types';
import { selectedLeadtimeSpans } from '@/features/forecasts/hooks/useFctForecasts';
import { useForecastQueryState } from '@/features/forecasts/hooks/useForecastQueryState';
import { useOrderData } from '@/features/forecasts/hooks/useOrderData';
import { useFctForecastsContext } from '@/features/forecasts/providers/FctForecastsProvider';
import {
  FCT_DATA_ZOOM,
  fctActualsLollipop,
  fctCategoryAxis,
  fctForecastBoundaryMarkLine,
  fctValueAxis,
} from '@/features/forecasts/utils/fctChartLayout';
import { formatIsoDateMd } from '@/utils/date';

type Props = { chartGroup?: string; title?: string };

const SERIES = ['actuals', 'incumbent', 'bfg'] as const;
type SeriesKey = (typeof SERIES)[number];

type BfgMetric = 'p50' | 'mean';

function buildChartOption(
  incumbent: Point[],
  bfg: Point[],
  actuals: EventBehaviorTimeseriesPoint[],
  visible: Record<SeriesKey, boolean>,
  labels: Record<SeriesKey, string>,
  bfgMetric: BfgMetric,
): EChartsOption {
  const { grid, colors } = graphConfig;

  const lastForecastIso = [...incumbent, ...bfg].reduce((max, p) => (p.iso > max ? p.iso : max), '');
  const visibleActuals = actuals.filter((a) => a.date && (!lastForecastIso || a.date <= lastForecastIso));

  const allDates = [
    ...new Set([...visibleActuals.map((a) => a.date), ...incumbent.map((p) => p.iso), ...bfg.map((p) => p.iso)]),
  ].sort();
  const axisLabels = allDates.map((d) => formatIsoDateMd(d));

  // Boundary sits on the first forecast point (earliest of either model) —
  // everything left of it is history.
  const firstForecastIso = [...incumbent, ...bfg].reduce((min, p) => (min === '' || p.iso < min ? p.iso : min), '');
  const boundaryLabel = firstForecastIso ? formatIsoDateMd(firstForecastIso) : null;

  const actualsByDate = new Map(visibleActuals.map((a) => [a.date, a.quantity ?? 0]));
  // Incumbent (ETS) only has a mean. BFG plots the selected metric (P50 by default).
  const incumbentByDate = new Map(incumbent.map((p) => [p.iso, p.mean]));
  const bfgByDate = new Map(bfg.map((p) => [p.iso, p[bfgMetric]]));

  const meanLine = (name: string, byDate: Map<string, number | null>, color: string, show: boolean) => ({
    name,
    type: 'line' as const,
    // Hidden series emit no data so they don't influence the y-axis extent.
    data: show ? allDates.map((d) => byDate.get(d) ?? null) : [],
    connectNulls: true,
    lineStyle: { color, width: 3 },
    itemStyle: { color },
    symbol: 'circle' as const,
    symbolSize: 5,
    showSymbol: true,
    emphasis: { focus: 'series' as const },
  });

  return {
    tooltip: { trigger: 'axis' },
    grid,
    xAxis: fctCategoryAxis(axisLabels),
    yAxis: fctValueAxis(),
    dataZoom: FCT_DATA_ZOOM,
    series: [
      ...fctActualsLollipop(
        labels.actuals,
        allDates.map((d) => (visible.actuals ? (actualsByDate.get(d) ?? null) : null)),
        colors.actuals,
      ),
      meanLine(labels.incumbent, incumbentByDate, INCUMBENT_COLOR, visible.incumbent),
      meanLine(labels.bfg, bfgByDate, BFG_COLOR, visible.bfg),
      fctForecastBoundaryMarkLine(boundaryLabel),
    ],
  } satisfies EChartsOption;
}

export function FctComparisonChart({ chartGroup, title }: Props) {
  const { state } = useForecastQueryState();
  const { bfg: bfgForecasts, incumbent: incumbentForecasts } = useFctForecastsContext();
  const { actuals } = useOrderData();

  const [visible, setVisible] = useState<Record<SeriesKey, boolean>>({ actuals: true, incumbent: true, bfg: true });
  // BFG metric basis for the comparison — P50 by default. Incumbent always uses
  // its mean (ETS exposes no percentiles).
  const [bfgMetric, setBfgMetric] = useState<BfgMetric>('p50');

  const ltsp = useMemo(() => selectedLeadtimeSpans(state.leadTime, state.span), [state.leadTime, state.span]);
  const incumbentPlotSpan = toModelLeadtimeSpans(INCUMBENT_MODEL_CONFIG, ltsp)[0] ?? null;
  const bfgPlotSpan = toModelLeadtimeSpans(BFG_MODEL_CONFIG, ltsp)[0] ?? null;

  const incumbentPoints = useMemo(
    () => (incumbentPlotSpan ? reduceToPoints(incumbentForecasts.series, incumbentPlotSpan) : []),
    [incumbentForecasts.series, incumbentPlotSpan],
  );
  const bfgPoints = useMemo(
    () => (bfgPlotSpan ? reduceToPoints(bfgForecasts.series, bfgPlotSpan) : []),
    [bfgForecasts.series, bfgPlotSpan],
  );

  // Render as soon as either model has points; each line fills in when its own
  // model finishes, so a slow model never blocks the one that's ready.
  const bothLoading = incumbentForecasts.isLoading && bfgForecasts.isLoading;
  const toggle = (key: SeriesKey) => setVisible((prev) => ({ ...prev, [key]: !prev[key] }));

  if (bothLoading) return <Loader />;
  if (!incumbentPoints.length && !bfgPoints.length) {
    // Nothing plottable yet but a model is still fetching — keep the spinner.
    if (incumbentForecasts.isLoading || bfgForecasts.isLoading) return <Loader />;
    return <div className="py-12 text-center text-sm text-gray-400">No forecast data for this selection</div>;
  }

  const labels: Record<SeriesKey, string> = {
    actuals: 'Actuals',
    incumbent: 'Incumbent',
    bfg: 'BFG v2.2',
  };
  const swatchColor: Record<SeriesKey, string> = {
    actuals: graphConfig.colors.actuals,
    incumbent: INCUMBENT_COLOR,
    bfg: BFG_COLOR,
  };

  const option = buildChartOption(incumbentPoints, bfgPoints, actuals, visible, labels, bfgMetric);
  const bfgMetricLabel = bfgMetric === 'p50' ? 'P50' : 'Mean';

  return (
    <>
      <div className="mb-2 flex flex-wrap items-center justify-between gap-x-6 gap-y-2">
        {title ? <h3 className="text-sm font-semibold text-gray-900">{title}</h3> : <span />}
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-slate-600">
          {SERIES.map((key) => (
            <LegendToggle
              key={key}
              label={labels[key]}
              color={swatchColor[key]}
              active={visible[key]}
              onClick={() => toggle(key)}
            />
          ))}
        </div>
      </div>
      <div className="mb-4 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-600">
        <span className="font-medium text-slate-700">
          Comparing — BFG: <span className="font-semibold text-slate-900">{bfgMetricLabel}</span> vs Incumbent:{' '}
          <span className="font-semibold text-slate-900">Mean</span>
        </span>
        <fieldset className="flex items-center gap-x-3" aria-label="BFG comparison metric">
          <span className="text-slate-500">BFG metric:</span>
          {(['p50', 'mean'] as const).map((m) => (
            <label key={m} className="flex cursor-pointer items-center gap-1">
              <input
                type="radio"
                name="bfg-metric"
                value={m}
                checked={bfgMetric === m}
                onChange={() => setBfgMetric(m)}
                className="cursor-pointer accent-[#274483]"
              />
              {m === 'p50' ? 'P50' : 'Mean'}
            </label>
          ))}
        </fieldset>
      </div>
      <ReactECharts
        opts={{ renderer: 'svg' }}
        option={option}
        notMerge
        style={{ height: 360 }}
        onChartReady={(chart) => {
          if (chartGroup) chart.group = chartGroup;
        }}
      />
    </>
  );
}
